import { Button, Modal, SegmentedControl, Switch, useSnackbar } from '@hue/design-system';
import { Download } from 'lucide-react';
import { useState } from 'react';
import { getApiError } from '../../lib/api.js';
import type { ExportFormat, ExportedReport } from './reports-api.js';

/// Exportar o DRE ou o Fluxo de caixa (ADR 0062, decisão 5). O arquivo leva o mesmo recorte da
/// tela; a escolha de levar os lançamentos é feita a cada vez, porque o sócio quer o resumo e o
/// contador, no fechamento, quer conferir.
export function ReportExportButton({
  report,
  scope,
  load,
}: {
  /// "o DRE", "o Fluxo de caixa": entra no título e no aviso.
  report: string;
  /// O recorte que vai no arquivo, em frases curtas.
  scope: string[];
  load: (format: ExportFormat, facts: boolean) => Promise<ExportedReport>;
}) {
  const snackbar = useSnackbar();
  const [open, setOpen] = useState(false);
  const [format, setFormat] = useState<ExportFormat>('xlsx');
  const [facts, setFacts] = useState(false);
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    try {
      const file = await load(format, format === 'xlsx' && facts);
      const url = URL.createObjectURL(file.blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.fileName;
      link.click();
      URL.revokeObjectURL(url);
      // O download em si não avisa nada em celular: o arquivo cai na pasta e a tela não muda.
      snackbar.success(`Exportado para ${file.fileName}.`);
      setOpen(false);
    } catch (error) {
      snackbar.failure(getApiError(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        <Download className="size-4" aria-hidden="true" />
        Exportar
      </Button>
      <Modal
        open={open}
        onOpenChange={(next) => {
          if (!busy) setOpen(next);
        }}
        title={`Exportar ${report}`}
        description="O arquivo sai com o mesmo recorte da tela, com todas as contas do plano abertas."
        className="max-w-lg"
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>
              Cancelar
            </Button>
            <Button onClick={() => void save()} disabled={busy}>
              {busy ? 'Gerando o arquivo…' : 'Baixar arquivo'}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-5">
          <ul className="flex flex-col gap-1 text-sm text-content-muted">
            {scope.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
          <SegmentedControl
            label="Formato"
            value={format}
            onChange={setFormat}
            options={[
              { value: 'xlsx', label: 'Planilha Excel' },
              { value: 'pdf', label: 'PDF' },
            ]}
          />
          {format === 'xlsx' ? (
            <div className="flex flex-col gap-1.5">
              <Switch checked={facts} onChange={setFacts}>
                Incluir os lançamentos de cada valor
              </Switch>
              <p className="text-xs text-content-muted">
                Acrescenta a aba Lançamentos, com cada despesa, receita e movimento do extrato que
                forma os números, para conferir. Pode demorar mais.
              </p>
            </div>
          ) : (
            <p className="text-xs text-content-muted">
              O PDF leva o resumo e a tabela, com o timbre da empresa. Para conferir os lançamentos,
              use a planilha.
            </p>
          )}
        </div>
      </Modal>
    </>
  );
}
